import { ConditionalFormItem, FormItem, FormItemType } from "../types/form";
import { hasFormImages } from "./formImages";
import { isLocalEnvironment, isTestEnvironment } from "./app";

export function getFormSelectOptions(options: string[]) {
    return options.map((option, index) => ({
        key: `${index}:${option}`,
        value: option,
        label: option,
        title: option,
    }));
}

export function getConditionalIndex(item: FormItem) {
    return item.conditionals?.findIndex((conditional) => conditional.equalsTo === item.value)
}

export function getMatchingConditional(item: FormItem) {
    const hasConditionals = !!item.conditionals?.length;
    const conditionalIndex = getConditionalIndex(item);
    const matchingConditional = hasConditionals && conditionalIndex !== undefined && conditionalIndex >= 0 ? item.conditionals![conditionalIndex] : undefined;

    return matchingConditional;
}

// Only choice questions trigger follow-ups: their answer is the option text on every app. Checkbox and
// number values differ between web and phones, so a trigger on them would not match reliably.
export function canHaveFollowUps(type: FormItemType) {
    return type === "radio" || type === "dropdown";
}

export function isFormItemAnswered(item: Pick<FormItem, "type" | "value">) {
    if (item.type === "checkbox") {
        return item.value === "true" || Number(item.value) > 0;
    }
    if (item.type === "image") {
        return hasFormImages(item.value);
    }
    return item.value !== undefined && item.value !== null && `${item.value}`.trim() !== "";
}

// Items in display order, with the follow-ups of the matching answer right after their trigger
export function getShownFormItems(items: FormItem[]): (FormItem | ConditionalFormItem)[] {
    return items.flatMap((item) => [item, ...(getMatchingConditional(item)?.content ?? [])]);
}

// Sections that ask for more answers than they got. A section runs from its header to the next one.
export function getSectionsMissingAnswers(items: FormItem[]): FormItem[] {
    const missing: FormItem[] = [];
    let section: FormItem | undefined;
    let answered = 0;
    const check = () => {
        if (section && answered < (section.minAnswers ?? 0)) {
            missing.push(section);
        }
    };
    for (const item of getShownFormItems(items)) {
        if (item.type === "section") {
            check();
            section = item as FormItem;
            answered = 0;
        } else if (item.type !== "label" && isFormItemAnswered(item)) {
            answered++;
        }
    }
    check();
    return missing;
}

export const preventMinus = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.code === "Minus") {
        event.preventDefault();
    }
}

export function isFormItemTypeRequirable(type: FormItemType) {
    if (type === "section"
        || type === "label"
        || type === "text"
        || type === "form_title"
        || type === "form_description") {
        return false;
    }
    return true;
}

export function isFormItemValueEmpty(item: Pick<FormItem, "type" | "value">) {
    if (item.type === "image") {
        return !hasFormImages(item.value);
    }
    // 0 is an answer
    return item.value === undefined || item.value === null || item.value === "";
}

export const formSubmissionLink = (submissionUid: string) => {
    return isLocalEnvironment()
        ? `http://localhost:3000/!/@f/${submissionUid}`
        : isTestEnvironment()
            ? `https://base.hellotracks.com/!/@f/${submissionUid}`
            : `https://live.hellotracks.com/!/@f/${submissionUid}`;
}
